import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Plus, Edit2, Trash2, Loader2, X, Save, Target, Zap, Award, Eye, TrendingUp, ShieldCheck, Star } from 'lucide-react';

const Container = styled.div`
  padding: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--glass-border);
  
  h2 { font-size: 1.5rem; display: flex; align-items: center; gap: 10px; }
`;

const Button = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: opacity 0.3s;
  
  &:hover { opacity: 0.9; }
  &.danger { background: #ef4444; }
  &.secondary { background: transparent; border: 1px solid var(--glass-border); color: var(--text-main); }
  &.save { background: #22c55e; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-card);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--glass-border);
  margin-bottom: 20px;
  
  th, td { padding: 12px; text-align: left; border-bottom: 1px solid var(--glass-border); }
  th { background: rgba(99, 102, 241, 0.05); font-weight: 600; color: var(--text-muted); }
  .actions { display: flex; gap: 10px; }
  button.icon-btn {
    background: transparent; border: none; color: var(--text-muted); cursor: pointer;
    display: flex; align-items: center; justify-content: center; padding: 6px; border-radius: 6px;
    &:hover { background: var(--input-bg); color: var(--text-main); }
    &.delete:hover { color: #ef4444; background: rgba(239, 68, 68, 0.1); }
  }
`;

const FormCard = styled.div`
  background: var(--bg-card);
  padding: 24px;
  border-radius: 16px;
  border: 1px solid var(--glass-border);
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  label { font-size: 0.9rem; color: var(--text-muted); }
  input, textarea, select {
    padding: 10px 14px; background: var(--input-bg); border: 1px solid var(--glass-border);
    border-radius: 8px; color: var(--text-main); font-family: inherit;
    &:focus { outline: none; border-color: var(--primary); }
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const Tab = styled.button`
  padding: 8px 16px;
  background: ${props => props.active ? 'var(--primary)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--text-muted)'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : 'var(--glass-border)'};
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.3s;
  &:hover { background: ${props => props.active ? 'var(--primary)' : 'rgba(255,255,255,0.05)'}; }
`;

const MultiLangRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 15px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--bg-card); width: 90%; max-width: 800px; max-height: 90vh; overflow-y: auto;
  border-radius: 16px; padding: 30px; border: 1px solid var(--glass-border);
`;

const ManageAbout = () => {
    const [activeTab, setActiveTab] = useState('stats');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState([]);
    const [mission, setMission] = useState({ title_en: '', title_tr: '', title_es: '', content_en: '', content_tr: '', content_es: '' });
    const [values, setValues] = useState([]);
    const [milestones, setMilestones] = useState([]);
    const [team, setTeam] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState(''); // stats, values, milestones, team
    const [currentItem, setCurrentItem] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    const apiBase = `${process.env.REACT_APP_API_URL || ''}/api/about`;
    const token = localStorage.getItem('adminToken');

    const fetchData = useCallback(async (type) => {
        try {
            const res = await fetch(`${apiBase}/${type}`);
            const data = await res.json();
            if (type === 'stats') setStats(data);
            if (type === 'mission') setMission(data || { title_en: '', title_tr: '', title_es: '', content_en: '', content_tr: '', content_es: '' });
            if (type === 'values') setValues(data);
            if (type === 'milestones') setMilestones(data);
            if (type === 'team') setTeam(data);
        } catch (err) {
            console.error(err);
        }
    }, [apiBase]);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetchData('stats'),
            fetchData('mission'),
            fetchData('values'),
            fetchData('milestones'),
            fetchData('team')
        ]).finally(() => setLoading(false));
    }, [fetchData]);

    const handleOpenModal = (type, item = null) => {
        setModalType(type);
        if (item) {
            setCurrentItem(item);
            setIsEditing(true);
        } else {
            if (type === 'stats') setCurrentItem({ value: '', label_en: '', label_tr: '', label_es: '', sort_order: 0 });
            if (type === 'values') setCurrentItem({ icon: 'Zap', title_en: '', title_tr: '', title_es: '', desc_en: '', desc_tr: '', desc_es: '', sort_order: 0 });
            if (type === 'milestones') setCurrentItem({ year: '', title_en: '', title_tr: '', title_es: '', desc_en: '', desc_tr: '', desc_es: '', sort_order: 0 });
            if (type === 'team') setCurrentItem({ name: '', role_en: '', role_tr: '', role_es: '', emoji: '', sort_order: 0 });
            setIsEditing(false);
        }
        setModalOpen(true);
    };

    const handleSaveItem = async (e) => {
        e.preventDefault();
        const url = isEditing ? `${apiBase}/${modalType}/${currentItem.id}` : `${apiBase}/${modalType}`;
        const method = isEditing ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(currentItem)
            });
            if (res.ok) {
                fetchData(modalType);
                setModalOpen(false);
            }
        } catch (err) { console.error(err); }
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            const res = await fetch(`${apiBase}/${type}/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) fetchData(type);
        } catch (err) { console.error(err); }
    };

    const handleSaveMission = async () => {
        try {
            const res = await fetch(`${apiBase}/mission`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(mission)
            });
            if (res.ok) alert('Mission updated!');
        } catch (err) { console.error(err); }
    };

    if (loading) return <Container><Loader2 className="spin" /> Loading About Management...</Container>;

    return (
        <Container>
            <h1>Manage About Page</h1>
            <TabContainer>
                <Tab active={activeTab === 'stats'} onClick={() => setActiveTab('stats')}>Stats</Tab>
                <Tab active={activeTab === 'mission'} onClick={() => setActiveTab('mission')}>Mission</Tab>
                <Tab active={activeTab === 'values'} onClick={() => setActiveTab('values')}>Values</Tab>
                <Tab active={activeTab === 'milestones'} onClick={() => setActiveTab('milestones')}>Milestones</Tab>
                <Tab active={activeTab === 'team'} onClick={() => setActiveTab('team')}>Team</Tab>
            </TabContainer>

            {activeTab === 'stats' && (
                <>
                    <SectionHeader>
                        <h2>Stats Section</h2>
                        <Button onClick={() => handleOpenModal('stats')}><Plus size={18} /> Add Stat</Button>
                    </SectionHeader>
                    <Table>
                        <thead><tr><th>Value</th><th>Label (EN)</th><th>Order</th><th>Actions</th></tr></thead>
                        <tbody>
                            {stats.map(s => (
                                <tr key={s.id}>
                                    <td>{s.value}</td>
                                    <td>{s.label_en}</td>
                                    <td>{s.sort_order}</td>
                                    <td className="actions">
                                        <button className="icon-btn" onClick={() => handleOpenModal('stats', s)}><Edit2 size={16} /></button>
                                        <button className="icon-btn delete" onClick={() => handleDelete('stats', s.id)}><Trash2 size={16} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}

            {activeTab === 'mission' && (
                <>
                    <SectionHeader><h2>Mission Section</h2></SectionHeader>
                    <FormCard>
                        <MultiLangRow>
                            <FormGroup><label>Title (EN)</label><input type="text" value={mission.title_en} onChange={e => setMission({ ...mission, title_en: e.target.value })} /></FormGroup>
                            <FormGroup><label>Title (TR)</label><input type="text" value={mission.title_tr} onChange={e => setMission({ ...mission, title_tr: e.target.value })} /></FormGroup>
                            <FormGroup><label>Title (ES)</label><input type="text" value={mission.title_es} onChange={e => setMission({ ...mission, title_es: e.target.value })} /></FormGroup>
                        </MultiLangRow>
                        <FormGroup><label>Content (EN)</label><textarea rows="4" value={mission.content_en} onChange={e => setMission({ ...mission, content_en: e.target.value })} /></FormGroup>
                        <FormGroup><label>Content (TR)</label><textarea rows="4" value={mission.content_tr} onChange={e => setMission({ ...mission, content_tr: e.target.value })} /></FormGroup>
                        <FormGroup><label>Content (ES)</label><textarea rows="4" value={mission.content_es} onChange={e => setMission({ ...mission, content_es: e.target.value })} /></FormGroup>
                        <Button className="save" onClick={handleSaveMission}><Save size={18} /> Save Mission</Button>
                    </FormCard>
                </>
            )}

            {activeTab === 'values' && (
                <>
                    <SectionHeader>
                        <h2>Values Section</h2>
                        <Button onClick={() => handleOpenModal('values')}><Plus size={18} /> Add Value</Button>
                    </SectionHeader>
                    <Table>
                        <thead><tr><th>Icon</th><th>Title (EN)</th><th>Order</th><th>Actions</th></tr></thead>
                        <tbody>
                            {values.map(v => (
                                <tr key={v.id}>
                                    <td>{v.icon}</td>
                                    <td>{v.title_en}</td>
                                    <td>{v.sort_order}</td>
                                    <td className="actions">
                                        <button className="icon-btn" onClick={() => handleOpenModal('values', v)}><Edit2 size={16} /></button>
                                        <button className="icon-btn delete" onClick={() => handleDelete('values', v.id)}><Trash2 size={16} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}

            {activeTab === 'milestones' && (
                <>
                    <SectionHeader>
                        <h2>Milestones Section</h2>
                        <Button onClick={() => handleOpenModal('milestones')}><Plus size={18} /> Add Milestone</Button>
                    </SectionHeader>
                    <Table>
                        <thead><tr><th>Year</th><th>Title (EN)</th><th>Order</th><th>Actions</th></tr></thead>
                        <tbody>
                            {milestones.map(m => (
                                <tr key={m.id}>
                                    <td>{m.year}</td>
                                    <td>{m.title_en}</td>
                                    <td>{m.sort_order}</td>
                                    <td className="actions">
                                        <button className="icon-btn" onClick={() => handleOpenModal('milestones', m)}><Edit2 size={16} /></button>
                                        <button className="icon-btn delete" onClick={() => handleDelete('milestones', m.id)}><Trash2 size={16} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}

            {activeTab === 'team' && (
                <>
                    <SectionHeader>
                        <h2>Team Section</h2>
                        <Button onClick={() => handleOpenModal('team')}><Plus size={18} /> Add Member</Button>
                    </SectionHeader>
                    <Table>
                        <thead><tr><th>Name</th><th>Role (EN)</th><th>Emoji</th><th>Order</th><th>Actions</th></tr></thead>
                        <tbody>
                            {team.map(t => (
                                <tr key={t.id}>
                                    <td>{t.name}</td>
                                    <td>{t.role_en}</td>
                                    <td>{t.emoji}</td>
                                    <td>{t.sort_order}</td>
                                    <td className="actions">
                                        <button className="icon-btn" onClick={() => handleOpenModal('team', t)}><Edit2 size={16} /></button>
                                        <button className="icon-btn delete" onClick={() => handleDelete('team', t.id)}><Trash2 size={16} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}

            {modalOpen && (
                <ModalOverlay onClick={() => setModalOpen(false)}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <h2>{isEditing ? 'Edit' : 'Add'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}</h2>
                            <button className="icon-btn" onClick={() => setModalOpen(false)}><X size={24} /></button>
                        </div>
                        <form onSubmit={handleSaveItem}>
                            {modalType === 'stats' && (
                                <>
                                    <FormGroup><label>Value (e.g. 150+)</label><input required type="text" value={currentItem.value} onChange={e => setCurrentItem({ ...currentItem, value: e.target.value })} /></FormGroup>
                                    <MultiLangRow>
                                        <FormGroup><label>Label (EN)</label><input required type="text" value={currentItem.label_en} onChange={e => setCurrentItem({ ...currentItem, label_en: e.target.value })} /></FormGroup>
                                        <FormGroup><label>Label (TR)</label><input type="text" value={currentItem.label_tr} onChange={e => setCurrentItem({ ...currentItem, label_tr: e.target.value })} /></FormGroup>
                                        <FormGroup><label>Label (ES)</label><input type="text" value={currentItem.label_es} onChange={e => setCurrentItem({ ...currentItem, label_es: e.target.value })} /></FormGroup>
                                    </MultiLangRow>
                                </>
                            )}
                            {modalType === 'values' && (
                                <>
                                    <FormGroup>
                                        <label>Icon (Lucide name: Zap, Award, Eye, TrendingUp, ShieldCheck, Star)</label>
                                        <select value={currentItem.icon} onChange={e => setCurrentItem({ ...currentItem, icon: e.target.value })}>
                                            <option value="Zap">Zap</option><option value="Award">Award</option><option value="Eye">Eye</option>
                                            <option value="TrendingUp">TrendingUp</option><option value="ShieldCheck">ShieldCheck</option><option value="Star">Star</option>
                                            <option value="Target">Target</option><option value="Code2">Code2</option>
                                        </select>
                                    </FormGroup>
                                    <MultiLangRow>
                                        <FormGroup><label>Title (EN)</label><input required type="text" value={currentItem.title_en} onChange={e => setCurrentItem({ ...currentItem, title_en: e.target.value })} /></FormGroup>
                                        <FormGroup><label>Title (TR)</label><input type="text" value={currentItem.title_tr} onChange={e => setCurrentItem({ ...currentItem, title_tr: e.target.value })} /></FormGroup>
                                        <FormGroup><label>Title (ES)</label><input type="text" value={currentItem.title_es} onChange={e => setCurrentItem({ ...currentItem, title_es: e.target.value })} /></FormGroup>
                                    </MultiLangRow>
                                    <FormGroup><label>Description (EN)</label><textarea required rows="2" value={currentItem.desc_en} onChange={e => setCurrentItem({ ...currentItem, desc_en: e.target.value })} /></FormGroup>
                                    <FormGroup><label>Description (TR)</label><textarea rows="2" value={currentItem.desc_tr} onChange={e => setCurrentItem({ ...currentItem, desc_tr: e.target.value })} /></FormGroup>
                                    <FormGroup><label>Description (ES)</label><textarea rows="2" value={currentItem.desc_es} onChange={e => setCurrentItem({ ...currentItem, desc_es: e.target.value })} /></FormGroup>
                                </>
                            )}
                            {modalType === 'milestones' && (
                                <>
                                    <FormGroup><label>Year</label><input required type="text" value={currentItem.year} onChange={e => setCurrentItem({ ...currentItem, year: e.target.value })} /></FormGroup>
                                    <MultiLangRow>
                                        <FormGroup><label>Title (EN)</label><input required type="text" value={currentItem.title_en} onChange={e => setCurrentItem({ ...currentItem, title_en: e.target.value })} /></FormGroup>
                                        <FormGroup><label>Title (TR)</label><input type="text" value={currentItem.title_tr} onChange={e => setCurrentItem({ ...currentItem, title_tr: e.target.value })} /></FormGroup>
                                        <FormGroup><label>Title (ES)</label><input type="text" value={currentItem.title_es} onChange={e => setCurrentItem({ ...currentItem, title_es: e.target.value })} /></FormGroup>
                                    </MultiLangRow>
                                    <FormGroup><label>Description (EN)</label><textarea required rows="2" value={currentItem.desc_en} onChange={e => setCurrentItem({ ...currentItem, desc_en: e.target.value })} /></FormGroup>
                                    <FormGroup><label>Description (TR)</label><textarea rows="2" value={currentItem.desc_tr} onChange={e => setCurrentItem({ ...currentItem, desc_tr: e.target.value })} /></FormGroup>
                                    <FormGroup><label>Description (ES)</label><textarea rows="2" value={currentItem.desc_es} onChange={e => setCurrentItem({ ...currentItem, desc_es: e.target.value })} /></FormGroup>
                                </>
                            )}
                            {modalType === 'team' && (
                                <>
                                    <FormGroup><label>Name</label><input required type="text" value={currentItem.name} onChange={e => setCurrentItem({ ...currentItem, name: e.target.value })} /></FormGroup>
                                    <MultiLangRow>
                                        <FormGroup><label>Role (EN)</label><input required type="text" value={currentItem.role_en} onChange={e => setCurrentItem({ ...currentItem, role_en: e.target.value })} /></FormGroup>
                                        <FormGroup><label>Role (TR)</label><input type="text" value={currentItem.role_tr} onChange={e => setCurrentItem({ ...currentItem, role_tr: e.target.value })} /></FormGroup>
                                        <FormGroup><label>Role (ES)</label><input type="text" value={currentItem.role_es} onChange={e => setCurrentItem({ ...currentItem, role_es: e.target.value })} /></FormGroup>
                                    </MultiLangRow>
                                    <FormGroup><label>Emoji</label><input type="text" value={currentItem.emoji} onChange={e => setCurrentItem({ ...currentItem, emoji: e.target.value })} /></FormGroup>
                                </>
                            )}
                            <FormGroup><label>Sort Order</label><input type="number" value={currentItem.sort_order} onChange={e => setCurrentItem({ ...currentItem, sort_order: parseInt(e.target.value) })} /></FormGroup>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                                <Button type="button" className="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                                <Button type="submit">Save Changes</Button>
                            </div>
                        </form>
                    </ModalContent>
                </ModalOverlay>
            )}
            <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
        </Container>
    );
};

export default ManageAbout;
